import { annotationsClient } from 'packages/lib/api/annotations/http-client';
import {
  TaskGroupsResp,
  taskGroupsRespZod,
} from 'packages/lib/api/annotations/contract/task-group';
import { chunk } from 'lodash';

/**
 * Maximum number of sampleGroupTokens to include in a single
 * `/task-groups/stats` request. Each token is joined into the query string, so
 * a large set of tokens produces a URL that exceeds the server's request-line
 * length limit (resulting in a failed call). We split the tokens into batches
 * of this size and merge the results. ~50 tokens keeps the URL comfortably
 * under common 2KB request-line limits.
 */
export const TASK_GROUP_STATS_TOKEN_CHUNK_SIZE = 50;

export const taskGroupsApi = {
  /**
   * Fetches task-group stats for the given sampleGroupTokens.
   *
   * The tokens are batched into multiple requests to avoid exceeding the
   * server's URL length limit, and the results are flattened into a single
   * array. An empty token list results in no requests and an empty array.
   */
  statsBySampleGroupTokens: async (
    sampleGroupTokens: string[],
    partner = 'None'
  ): Promise<TaskGroupsResp> => {
    const batches = chunk(sampleGroupTokens, TASK_GROUP_STATS_TOKEN_CHUNK_SIZE);
    const results = await Promise.all(
      batches.map((tokens) =>
        annotationsClient.get('/task-groups/stats', {
          schema: taskGroupsRespZod,
          query: {
            partner,
            sampleGroupToken: tokens.join(','),
          },
        })
      )
    );
    return results.flat();
  },
};
